import React, { useState, useMemo, useCallback } from 'react';
import './ScheduleReminders.css';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  calculateAllScheduleData,
  generateGoogleMapsUrl,
  getWhatsAppDirectUrl,
  cleanPhoneNumber,
  generateGoogleMapsEmbedUrl,
  formatScheduleDateDisplay
} from '../../utils/scheduleUtils';

/**
 * ScheduleReminders
 * Teslimat Hatırlatıcısı, Keşif Hatırlatıcısı ve Akıllı Bölge / Rota Planlama Merkezi.
 * Accordion (açılır/kapanır), tam responsive, Google Maps adres pinleme ve keşif tamamlandı işaretleme desteği ile.
 */
const ScheduleReminders = ({
  customers = [],
  deliveryReminders = null,
  inspectionReminders = null,
  onSelectCustomer = null,
  onToggleInspection = null,
  isStandalonePage = false
}) => {
  const { t, language } = useLanguage();
  // Accordion açık/kapalı durumu (Sayfa modunda daima açık, dashboard modunda localStorage hatırlar)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (isStandalonePage) return false;
    return localStorage.getItem('schedule_reminders_collapsed') === 'true';
  });

  const toggleAccordion = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('schedule_reminders_collapsed', String(next));
      return next;
    });
  };

  // Aktif sekme: 'regional' | 'timeline' | 'montaj' | 'kesif'
  const [activeTab, setActiveTab] = useState('regional');
  // Bölge arama filtresi
  const [regionFilter, setRegionFilter] = useState('');
  // Tarih aralığı filtresi: 'all' | 'today' | 'week'
  const [timeFilter, setTimeFilter] = useState('all');
  // Yapılan (tamamlanan) keşifleri gösterme seçeneği
  const [showCompletedKesif, setShowCompletedKesif] = useState(false);
  // Bölge rotasında durakları listeleme durumu: regionKey | null
  const [expandedRouteGroupId, setExpandedRouteGroupId] = useState(null);

  // Müşterilerden tüm randevu ve bölge verilerini hesapla
  const scheduleData = useMemo(() => {
    return calculateAllScheduleData(customers);
  }, [customers]);

  const {
    completedInspections = [],
    allEvents,
    regionalGroups,
    stats
  } = scheduleData;

  // Metin / şehir / müşteri arama filtresi
  const filterBySearch = useCallback((events) => {
    if (!regionFilter) return events;
    const query = regionFilter.toLocaleLowerCase('tr-TR');
    return events.filter(item =>
      (item.name && item.name.toLocaleLowerCase('tr-TR').includes(query)) ||
      (item.city && item.city.toLocaleLowerCase('tr-TR').includes(query)) ||
      (item.address && item.address.toLocaleLowerCase('tr-TR').includes(query)) ||
      (item.regionDisplay && item.regionDisplay.toLocaleLowerCase('tr-TR').includes(query))
    );
  }, [regionFilter]);

  // Zaman filtresine göre filtreleme
  const filterByTime = useCallback((events) => {
    if (timeFilter === 'all') return events;
    if (timeFilter === 'today') {
      return events.filter(e => e.category === 'today');
    }
    if (timeFilter === 'week') {
      return events.filter(e => e.category === 'today' || e.category === 'tomorrow' || e.category === 'thisWeek');
    }
    if (timeFilter === 'overdue') {
      return events.filter(e => e.category === 'overdue');
    }
    return events;
  }, [timeFilter]);

  // Filtre durumunu özetleyen metin
  const getFilterSummaryText = useCallback(() => {
    let filterLabel = language === 'nl' ? 'Alle' : 'Tümü';
    if (timeFilter === 'today') filterLabel = language === 'nl' ? 'Vandaag' : 'Bugün';
    else if (timeFilter === 'week') filterLabel = language === 'nl' ? 'Deze Week' : 'Bu Hafta';
    else if (timeFilter === 'overdue') filterLabel = language === 'nl' ? 'Vertraagd' : 'Gecikenler';
    return regionFilter ? `${filterLabel} • "${regionFilter}"` : filterLabel;
  }, [timeFilter, regionFilter, language]);

  // Filtrelenmiş bölgeler (Bölge & Rota Planlayıcı sekmesi)
  const filteredRegionalGroups = useMemo(() => {
    return regionalGroups.filter(group => {
      // Şehir / müşteri arama filtresi
      if (regionFilter) {
        const query = regionFilter.toLocaleLowerCase('tr-TR');
        const matchName = group.regionDisplay.toLocaleLowerCase('tr-TR').includes(query);
        const matchCust = [...group.kesifList, ...group.montajList].some(item =>
          (item.name && item.name.toLocaleLowerCase('tr-TR').includes(query)) ||
          (item.city && item.city.toLocaleLowerCase('tr-TR').includes(query)) ||
          (item.address && item.address.toLocaleLowerCase('tr-TR').includes(query))
        );
        if (!matchName && !matchCust) return false;
      }

      // Zaman filtresi
      if (timeFilter !== 'all') {
        const hasMatchingKesif = filterByTime(group.kesifList).length > 0;
        const hasMatchingMontaj = filterByTime(group.montajList).length > 0;
        if (!hasMatchingKesif && !hasMatchingMontaj) return false;
      }

      return true;
    });
  }, [regionalGroups, regionFilter, timeFilter, filterByTime]);

  // Tarih ve aramaya göre süzülmüş tüm etkinlikler (Ajanda sekmesi)
  const filteredTimelineEvents = useMemo(() => {
    return filterBySearch(filterByTime(allEvents));
  }, [allEvents, filterByTime, filterBySearch]);

  // Tarih ve aramaya göre süzülmüş montaj etkinlikleri
  const filteredMontajEvents = useMemo(() => {
    return filterBySearch(filterByTime(allEvents.filter(e => e.type === 'montaj')));
  }, [allEvents, filterByTime, filterBySearch]);

  // Tarih ve aramaya göre süzülmüş keşif etkinlikleri
  const filteredKesifEvents = useMemo(() => {
    return filterBySearch(filterByTime(allEvents.filter(e => e.type === 'kesif')));
  }, [allEvents, filterByTime, filterBySearch]);

  // Harita ve pinleme için geçerli adresi olan etkinlikler
  const visibleAddresses = useMemo(() => {
    return filteredTimelineEvents.filter(e => e.address && e.address.trim().length > 0);
  }, [filteredTimelineEvents]);

  const visibleKesifAddresses = useMemo(() => {
    return visibleAddresses.filter(e => e.type === 'kesif');
  }, [visibleAddresses]);

  const visibleMontajAddresses = useMemo(() => {
    return visibleAddresses.filter(e => e.type === 'montaj');
  }, [visibleAddresses]);

  // Çoklu adresler için Google Haritalar pinleme & rota URL'i
  const createMultiPinMapsUrl = useCallback((items) => {
    const valid = items
      .map(i => i.address ? `${i.address}, ${i.city || ''} Netherlands`.trim() : '')
      .filter(Boolean);

    if (valid.length === 0) return null;
    if (valid.length === 1) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(valid[0])}`;
    }
    const origin = encodeURIComponent(valid[0]);
    const destination = encodeURIComponent(valid[valid.length - 1]);
    const waypoints = valid.slice(1, -1).slice(0, 8).map(encodeURIComponent).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    return url;
  }, []);

  const allPinsUrl = useMemo(() => createMultiPinMapsUrl(visibleAddresses), [visibleAddresses, createMultiPinMapsUrl]);
  const kesifPinsUrl = useMemo(() => createMultiPinMapsUrl(visibleKesifAddresses), [visibleKesifAddresses, createMultiPinMapsUrl]);
  const montajPinsUrl = useMemo(() => createMultiPinMapsUrl(visibleMontajAddresses), [visibleMontajAddresses, createMultiPinMapsUrl]);

  // Canlı harita önizlemesi için arama sorgusu
  const mapEmbedQuery = useMemo(() => {
    if (regionFilter && regionFilter.trim()) {
      return `${regionFilter.trim()}, Netherlands`;
    }
    if (visibleAddresses.length > 0) {
      const first = visibleAddresses[0];
      return `${first.city || first.address}, Netherlands`;
    }
    return 'Netherlands';
  }, [regionFilter, visibleAddresses]);

  // Toplam planlanabilir etkinlik yoksa gösterme
  const totalActiveEvents = stats.totalMontajCount + stats.totalKesifCount;
  if (totalActiveEvents === 0 && completedInspections.length === 0) {
    return null;
  }

  // Müşteri seçimi / inceleme
  const handleCustomerClick = (customerId, customerName) => {
    if (onSelectCustomer) {
      onSelectCustomer(customerId, customerName);
    }
  };

  return (
    <div className={`schedule-reminders-container ${isCollapsed ? 'is-collapsed' : ''}`}>
      {/* Üst Başlık ve İstatistik Şeridi (Accordion Header) */}
      <div
        className="schedule-header-wrapper"
        onClick={toggleAccordion}
        title={isCollapsed ? (language === 'nl' ? 'Klik om Afspraak- & Routeplanner te openen' : 'Randevu & Rota Planlayıcıyı açmak için tıklayın') : (language === 'nl' ? 'Klik om in te klappen' : 'Daraltmak için tıklayın')}
        role="button"
        tabIndex={0}
      >
        <div className="schedule-title-area">
          <div className="schedule-icon-badge">
            <i className="fas fa-route"></i>
          </div>
          <div className="schedule-title-text-group">
            <div className="schedule-main-title">
              <span>{language === 'nl' ? 'Afspraak- & Routeplanning Centrum' : 'Randevu & Rota Planlama Merkezi'}</span>
              <span className="accordion-indicator-pill">
                <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}></i>
                <span className="indicator-label">{isCollapsed ? (language === 'nl' ? 'Uitklappen' : 'Genişlet') : (language === 'nl' ? 'Inklappen' : 'Daralt')}</span>
              </span>
            </div>
            <p className="schedule-sub-title">
              {language === 'nl' ? 'Inmeetafspraken, montageleveringen en regiekansen om op dezelfde dag te combineren' : 'Keşif randevuları, montaj teslimatları ve aynı güne birleştirilebilir bölge fırsatları'}
            </p>
          </div>
        </div>

        {/* Özet İstatistik Rozetleri */}
        <div className="schedule-stats-strip" onClick={(e) => e.stopPropagation()}>
          {stats.opportunityCount > 0 && (
            <div
              className={`stat-pill pill-opportunity ${activeTab === 'regional' && !isCollapsed ? 'active-pill' : ''}`}
              onClick={() => {
                if (isCollapsed) toggleAccordion();
                setActiveTab('regional');
              }}
              title={language === 'nl' ? "Regio's met zowel inmetingen als montages" : "Aynı şehirde hem keşif hem montaj olan bölgeler"}
            >
              <i className="fas fa-sparkles"></i>
              <span><strong>{stats.opportunityCount}</strong> {language === 'nl' ? 'Regiokans' : 'Bölge Fırsatı'}</span>
            </div>
          )}

          <div
            className={`stat-pill pill-kesif ${activeTab === 'kesif' && !isCollapsed ? 'active-pill' : ''}`}
            onClick={() => {
              if (isCollapsed) toggleAccordion();
              setActiveTab('kesif');
            }}
          >
            <i className="fas fa-ruler-combined"></i>
            <span><strong>{stats.totalKesifCount}</strong> {language === 'nl' ? 'Inmeten' : 'Keşif'}</span>
          </div>

          <div
            className={`stat-pill pill-montaj ${activeTab === 'montaj' && !isCollapsed ? 'active-pill' : ''}`}
            onClick={() => {
              if (isCollapsed) toggleAccordion();
              setActiveTab('montaj');
            }}
          >
            <i className="fas fa-truck"></i>
            <span><strong>{stats.totalMontajCount}</strong> {language === 'nl' ? 'Montage' : 'Montaj'}</span>
          </div>

          {stats.overdueTotal > 0 && (
            <div
              className={`stat-pill pill-overdue ${timeFilter === 'overdue' ? 'active-pill' : ''}`}
              onClick={() => {
                if (isCollapsed) toggleAccordion();
                setTimeFilter(prev => prev === 'overdue' ? 'all' : 'overdue');
              }}
              style={{ cursor: 'pointer' }}
              title={language === 'nl' ? 'Klik om vertraagde afspraken te filteren' : 'Geciken randevuları filtrelemek için tıklayın'}
            >
              <i className="fas fa-exclamation-triangle"></i>
              <span><strong>{stats.overdueTotal}</strong> {language === 'nl' ? 'Vertraagd' : 'Geciken'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Accordion Gövdesi (Açılır/Kapanır) */}
      {!isCollapsed && (
        <div className="schedule-accordion-content">
          {/* Navigasyon / Sekmeler & Filtreler */}
          <div className="schedule-toolbar">
            <div className="schedule-tabs-nav">
              <button
                type="button"
                className={`schedule-tab-btn ${activeTab === 'regional' ? 'active' : ''}`}
                onClick={() => setActiveTab('regional')}
              >
                <i className="fas fa-map-marked-alt"></i>
                <span>{t('planning.regionalPlanner', 'Bölge & Rota Planlayıcı')}</span>
                <span className="tab-badge">{filteredRegionalGroups.length}</span>
                {stats.opportunityCount > 0 && (
                  <span className="tab-badge-glow" title={`${stats.opportunityCount} ${t('planning.sameDayOpportunity', 'Aynı Gün Fırsatı!')}`}>
                    <i className="fas fa-sparkles"></i>
                  </span>
                )}
              </button>

              <button
                type="button"
                className={`schedule-tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>{t('planning.timeline', 'Ajanda (Tümü)')}</span>
                <span className="tab-badge">{filteredTimelineEvents.length}</span>
              </button>

              <button
                type="button"
                className={`schedule-tab-btn ${activeTab === 'montaj' ? 'active' : ''}`}
                onClick={() => setActiveTab('montaj')}
              >
                <i className="fas fa-truck"></i>
                <span>{t('planning.montajTab', 'Montaj & Teslimat')}</span>
                <span className="tab-badge">{filteredMontajEvents.length}</span>
              </button>

              <button
                type="button"
                className={`schedule-tab-btn ${activeTab === 'kesif' ? 'active' : ''}`}
                onClick={() => setActiveTab('kesif')}
              >
                <i className="fas fa-ruler-combined"></i>
                <span>{t('planning.kesifTab', 'Keşif Randevuları')}</span>
                <span className="tab-badge">{filteredKesifEvents.length}</span>
              </button>

              <button
                type="button"
                className={`schedule-tab-btn ${activeTab === 'map' ? 'active' : ''}`}
                onClick={() => setActiveTab('map')}
              >
                <i className="fas fa-map-pin"></i>
                <span>{t('planning.mapPinsTab', 'Harita & Pinler')}</span>
                <span className="tab-badge">{visibleAddresses.length}</span>
              </button>
            </div>

            {/* Hızlı Filtre Barı & Harita Aksiyonu */}
            <div className="schedule-filters-row">
              <div className="schedule-search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder={t('planning.searchPlaceholder', 'Şehir veya müşteri ara...')}
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                />
                {regionFilter && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => setRegionFilter('')}
                    title={t('common.clear', 'Aramayı temizle')}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              <div className="schedule-time-filter">
                <span className="filter-label"><i className="fas fa-filter"></i> {t('common.date', 'Tarih')}:</span>
                <button
                  type="button"
                  className={`filter-chip ${timeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setTimeFilter('all')}
                >
                  {t('planning.filterAll', 'Tümü')}
                </button>
                <button
                  type="button"
                  className={`filter-chip ${timeFilter === 'today' ? 'active' : ''}`}
                  onClick={() => setTimeFilter('today')}
                >
                  {t('common.today', 'Bugün')}
                </button>
                <button
                  type="button"
                  className={`filter-chip ${timeFilter === 'week' ? 'active' : ''}`}
                  onClick={() => setTimeFilter('week')}
                >
                  {t('common.thisWeek', 'Bu Hafta')}
                </button>
                {stats.overdueTotal > 0 && (
                  <button
                    type="button"
                    className={`filter-chip filter-chip-overdue ${timeFilter === 'overdue' ? 'active' : ''}`}
                    onClick={() => setTimeFilter(prev => prev === 'overdue' ? 'all' : 'overdue')}
                    title={t('planning.overdue', 'Gecikmiş randevular')}
                  >
                    <i className="fas fa-exclamation-triangle"></i> {t('planning.filterOverdue', 'Geciken')} ({stats.overdueTotal})
                  </button>
                )}
              </div>

              {visibleAddresses.length > 0 && allPinsUrl && (
                <div className="schedule-pin-action-group">
                  <a
                    href={allPinsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="toolbar-pin-btn"
                    title={language === 'nl' ? 'Pin en open alle weergegeven adressen in Google Maps' : "Görüntülenen tüm adresleri Google Haritalar'da pinle ve aç"}
                  >
                    <i className="fas fa-map-pin"></i>
                    <span>{language === 'nl' ? 'Vastpinnen op Kaart' : 'Haritada Pinle'} ({visibleAddresses.length})</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* ===================== TAB 1: BÖLGE & ROTA PLANLAYICI ===================== */}
          {activeTab === 'regional' && (
            <div className="regional-view-container">
              {stats.opportunityCount > 0 && !regionFilter && (
                <div className="opportunity-banner">
                  <div className="opp-icon">
                    <i className="fas fa-lightbulb"></i>
                  </div>
                  <div className="opp-text">
                    {language === 'nl' ? (
                      <><strong>Slimme Routekans:</strong> In {stats.opportunityCount} verschillende regio('s) heeft u zowel inmeet- als montageafspraken. Door deze bezoeken op dezelfde dag te plannen, kunt u reistijd en brandstofkosten minimaliseren.</>
                    ) : (
                      <><strong>Akıllı Rota Fırsatı:</strong> {stats.opportunityCount} farklı bölgede hem keşif hem montaj randevunuz bulunuyor. Bu ziyaretleri aynı güne planlayarak seyahat süresini ve yakıt masraflarını minimize edebilirsiniz.</>
                    )}
                  </div>
                </div>
              )}

              {filteredRegionalGroups.length === 0 ? (
                <div className="empty-schedule-state">
                  <i className="fas fa-map-marker-slash"></i>
                  <p>{language === 'nl' ? 'Geen geplande regio of afspraak gevonden die voldoet aan het filter.' : 'Filtreye uygun planlanmış bölge veya randevu bulunamadı.'}</p>
                </div>
              ) : (
                <div className="regional-cards-grid">
                  {filteredRegionalGroups.map(group => {
                    const visibleKesif = filterBySearch(filterByTime(group.kesifList));
                    const visibleMontaj = filterBySearch(filterByTime(group.montajList));
                    const isOpp = visibleKesif.length > 0 && visibleMontaj.length > 0;

                    // Bu bölgede süzülmüş tüm duraklar (zaman ve arama filtresine göre sıralı)
                    const groupStops = [...visibleKesif, ...visibleMontaj];
                    const groupAddresses = Array.from(new Set(
                      groupStops
                        .map(item => item.address ? `${item.address}${item.city ? `, ${item.city}` : ''} Netherlands`.trim() : '')
                        .filter(Boolean)
                    ));

                    // Konumdan rota URL (Google Haritalar cihazın mevcut GPS konumunu origin alır)
                    const myLocationRouteUrl = groupAddresses.length > 0
                      ? generateGoogleMapsUrl(groupAddresses, { startFromStore: false })
                      : null;

                    // Mağazadan rota URL (Ebrar Gordijnen mağazasından başlar)
                    const storeRouteUrl = groupAddresses.length > 0
                      ? generateGoogleMapsUrl(groupAddresses, { startFromStore: true })
                      : null;

                    const isStopsExpanded = expandedRouteGroupId === group.regionKey;

                    const regionTitle = group.regionDisplay === 'Diğer / Belirtilmemiş'
                      ? (language === 'nl' ? 'Overig / Niet opgegeven' : 'Diğer / Belirtilmemiş')
                      : (group.regionDisplay.startsWith('Bölge ') ? group.regionDisplay.replace('Bölge ', language === 'nl' ? 'Regio ' : 'Bölge ') : group.regionDisplay);

                    return (
                      <div
                        key={group.regionKey}
                        className={`regional-card ${isOpp ? 'card-has-opportunity' : ''}`}
                      >
                        {/* Kart Başlığı */}
                        <div className="regional-card-header">
                          <div className="region-name-group">
                            <i className="fas fa-map-marker-alt region-pin"></i>
                            <h4 className="region-title">{regionTitle}</h4>
                            <span className="region-total-badge">
                              {visibleKesif.length + visibleMontaj.length} {language === 'nl' ? (visibleKesif.length + visibleMontaj.length === 1 ? 'Afspraak' : 'Afspraken') : 'Randevu'}
                            </span>
                            {isOpp && (
                              <span className="opp-tag">
                                <i className="fas fa-sparkles"></i>
                                {language === 'nl' ? 'Zelfde Dag Kans!' : 'Aynı Gün Fırsatı!'}
                              </span>
                            )}
                          </div>

                          {/* Rota ve Navigasyon Aksiyonları */}
                          {groupAddresses.length > 0 && (
                            <div className="region-route-actions">
                              <a
                                href={myLocationRouteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="maps-route-btn route-btn-location"
                                title="Google Haritalar"
                              >
                                <i className="fas fa-location-arrow"></i>
                                <span>{t('planning.routeFromLocation', 'Konumumdan Rota')} ({groupAddresses.length})</span>
                              </a>

                              <a
                                href={storeRouteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="maps-route-sub-btn"
                                title="Ebrar Gordijnen"
                              >
                                <i className="fas fa-store"></i>
                                <span>{t('planning.routeFromStore', 'Mağazadan')}</span>
                              </a>

                              <button
                                type="button"
                                className={`route-stops-toggle-btn ${isStopsExpanded ? 'active' : ''}`}
                                onClick={() => setExpandedRouteGroupId(prev => prev === group.regionKey ? null : group.regionKey)}
                              >
                                <i className={`fas ${isStopsExpanded ? 'fa-chevron-up' : 'fa-list-ol'}`}></i>
                                <span>{isStopsExpanded ? t('planning.closeStops', 'Durakları Kapat') : t('planning.listStops', 'Durakları Listele')}</span>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Genişletilmiş Duraklar Listesi (Sıralı Duraklar & Tek Tıkla Konumdan Navigasyon) */}
                        {isStopsExpanded && (
                          <div className="route-stops-panel">
                            <div className="route-stops-panel-header">
                              <div className="stops-panel-title">
                                <i className="fas fa-route"></i>
                                <strong>{t('planning.stopsSequence', 'Rota Durakları Sıralaması')}</strong>
                                <span className="stops-count-tag">{groupStops.length} {t('common.total', 'Durak')}</span>
                              </div>
                              <span className="stops-panel-hint">
                                <i className="fas fa-info-circle"></i> {t('planning.stopsHint', 'Müşteriye gitmek için yanındaki butona tıklayarak navigasyon başlatabilirsiniz.')}
                              </span>
                            </div>

                            <div className="route-stops-list">
                              {groupStops.map((stopItem, stopIndex) => {
                                const stopAddress = stopItem.address ? `${stopItem.address}${stopItem.city ? `, ${stopItem.city}` : ''} Netherlands`.trim() : '';
                                const stopNavUrl = stopAddress ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stopAddress)}&travelmode=driving` : null;
                                const isItemKesif = stopItem.type === 'kesif';
                                const cleanPhone = cleanPhoneNumber(stopItem.phone);
                                const waUrl = getWhatsAppDirectUrl(stopItem.phone, stopItem.name, stopItem.type);

                                return (
                                  <div key={stopItem.id || stopIndex} className="route-stop-row">
                                    <div className="stop-order-badge">
                                      {stopIndex + 1}
                                    </div>
                                    <div className="stop-info-block">
                                      <div className="stop-top-line">
                                        <button
                                          type="button"
                                          className="stop-customer-btn"
                                          onClick={() => handleCustomerClick && handleCustomerClick(stopItem.customerId, stopItem.name)}
                                          title={language === 'nl' ? 'Klant openen' : 'Müşteri kartını aç'}
                                        >
                                          {stopItem.name}
                                          <i className="fas fa-external-link-alt"></i>
                                        </button>
                                        <span className={`type-badge ${isItemKesif ? 'badge-kesif' : 'badge-montaj'}`}>
                                          <i className={`fas ${stopItem.typeIcon || (isItemKesif ? 'fa-ruler-combined' : 'fa-truck')}`}></i>
                                          {isItemKesif ? (language === 'nl' ? 'Inmeten' : 'Keşif') : (language === 'nl' ? 'Montage' : 'Montaj')}
                                        </span>
                                        <span className="stop-date-pill">
                                          <i className="fas fa-calendar-day"></i>
                                          {formatScheduleDateDisplay(stopItem.rawDate || stopItem.formattedDate, language)}
                                        </span>
                                      </div>
                                      <div className="stop-address-line">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <span>{stopAddress || (language === 'nl' ? 'Geen adres opgegeven' : 'Adres belirtilmemiş')}</span>
                                      </div>
                                    </div>
                                    <div className="stop-actions-block">
                                      {stopNavUrl && (
                                        <a
                                          href={stopNavUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="stop-nav-action-btn"
                                          title="Google Haritalar"
                                        >
                                          <i className="fas fa-location-arrow"></i>
                                          <span>{t('planning.goToStop', 'Konumdan Git')}</span>
                                        </a>
                                      )}
                                      {cleanPhone && (
                                        <a
                                          href={`tel:${cleanPhone}`}
                                          className="stop-icon-btn call"
                                          title={language === 'nl' ? 'Bellen' : 'Telefon ile ara'}
                                        >
                                          <i className="fas fa-phone"></i>
                                        </a>
                                      )}
                                      {waUrl && (
                                        <a
                                          href={waUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="stop-icon-btn wa"
                                          title={language === 'nl' ? 'WhatsApp bericht sturen' : 'WhatsApp mesajı gönder'}
                                        >
                                          <i className="fab fa-whatsapp"></i>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* İki Kolon / Blok: Keşifler ve Montajlar */}
                        <div className="regional-card-columns">
                          {/* Sol Kolon: Keşifler */}
                          <div className="regional-col kesif-col">
                            <div className="col-header">
                              <i className="fas fa-ruler-combined"></i>
                              <span>{language === 'nl' ? 'Inmeten & Advies' : 'Keşif & Ölçüm'}</span>
                              <span className="col-count">{visibleKesif.length}</span>
                            </div>
                            {visibleKesif.length === 0 ? (
                              <div className="col-empty">{language === 'nl' ? 'Geen geplande inmeting in deze regio' : 'Bu bölgede planlı keşif yok'}</div>
                            ) : (
                              <div className="items-list">
                                {visibleKesif.map(item => (
                                  <ScheduleItemCard
                                    key={item.id}
                                    item={item}
                                    onCustomerClick={handleCustomerClick}
                                    onToggleInspection={onToggleInspection}
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Sağ Kolon: Montaj / Teslimatlar */}
                          <div className="regional-col montaj-col">
                            <div className="col-header">
                              <i className="fas fa-truck"></i>
                              <span>{language === 'nl' ? 'Montage & Levering' : 'Teslimat & Montaj'}</span>
                              <span className="col-count">{visibleMontaj.length}</span>
                            </div>
                            {visibleMontaj.length === 0 ? (
                              <div className="col-empty">{language === 'nl' ? 'Geen geplande montage in deze regio' : 'Bu bölgede planlı montaj yok'}</div>
                            ) : (
                              <div className="items-list">
                                {visibleMontaj.map(item => (
                                  <ScheduleItemCard
                                    key={item.id}
                                    item={item}
                                    onCustomerClick={handleCustomerClick}
                                    onToggleInspection={onToggleInspection}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ===================== TAB 2: AJANDA / ZAMAN ÇİZELGESİ ===================== */}
          {activeTab === 'timeline' && (
            <div className="timeline-view-container">
              {filteredTimelineEvents.length === 0 ? (
                <div className="empty-schedule-state">
                  <i className="fas fa-calendar-check"></i>
                  <p>{language === 'nl' ? `Geen afspraken gevonden die voldoen aan de geselecteerde filters (${getFilterSummaryText()}).` : `Seçilen filtrelere uygun (${getFilterSummaryText()}) randevu bulunmuyor.`}</p>
                </div>
              ) : (
                <>
                  <TimelineSection
                    title={language === 'nl' ? 'Vertraagde Afspraken' : 'Gecikmiş Randevular'}
                    icon="fa-exclamation-triangle"
                    variant="overdue"
                    events={filteredTimelineEvents.filter(e => e.category === 'overdue')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Vandaag' : 'Bugün'}
                    icon="fa-clock"
                    variant="today"
                    events={filteredTimelineEvents.filter(e => e.category === 'today')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Morgen' : 'Yarın'}
                    icon="fa-calendar-day"
                    variant="tomorrow"
                    events={filteredTimelineEvents.filter(e => e.category === 'tomorrow')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Deze Week (Komende 7 Dagen)' : 'Bu Hafta (Önümüzdeki 7 Gün)'}
                    icon="fa-calendar-week"
                    variant="thisWeek"
                    events={filteredTimelineEvents.filter(e => e.category === 'thisWeek')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Aankomende Afspraken' : 'Gelecek Randevular'}
                    icon="fa-calendar-alt"
                    variant="upcoming"
                    events={filteredTimelineEvents.filter(e => e.category === 'upcoming')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                </>
              )}
            </div>
          )}

          {/* ===================== TAB 3: SADECE MONTAJ / TESLİMAT ===================== */}
          {activeTab === 'montaj' && (
            <div className="timeline-view-container">
              {filteredMontajEvents.length === 0 ? (
                <div className="empty-schedule-state">
                  <i className="fas fa-calendar-check"></i>
                  <p>{language === 'nl' ? `Geen levering / montage afspraak gevonden die voldoet aan de geselecteerde filters (${getFilterSummaryText()}).` : `Seçilen filtrelere uygun (${getFilterSummaryText()}) teslimat / montaj randevusu bulunmuyor.`}</p>
                </div>
              ) : (
                <>
                  <TimelineSection
                    title={language === 'nl' ? 'Vertraagde Montages' : 'Geciken Teslimatlar'}
                    icon="fa-exclamation-triangle"
                    variant="overdue"
                    events={filteredMontajEvents.filter(e => e.category === 'overdue')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Vandaag te Leveren' : 'Bugün Teslim Edilecek'}
                    icon="fa-clock"
                    variant="today"
                    events={filteredMontajEvents.filter(e => e.category === 'today')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Morgen te Leveren' : 'Yarın Teslim Edilecek'}
                    icon="fa-calendar-day"
                    variant="tomorrow"
                    events={filteredMontajEvents.filter(e => e.category === 'tomorrow')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Deze Week te Leveren' : 'Bu Hafta Teslim Edilecek'}
                    icon="fa-calendar-week"
                    variant="thisWeek"
                    events={filteredMontajEvents.filter(e => e.category === 'thisWeek')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Aankomende Montages' : 'Gelecek Teslimatlar'}
                    icon="fa-calendar-alt"
                    variant="upcoming"
                    events={filteredMontajEvents.filter(e => e.category === 'upcoming')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                </>
              )}
            </div>
          )}

          {/* ===================== TAB 4: SADECE KEŞİF ===================== */}
          {activeTab === 'kesif' && (
            <div className="timeline-view-container">
              {/* Tamamlananları Göster / Gizle Kontrolü */}
              {completedInspections.length > 0 && (
                <div className="completed-toggle-row">
                  <label className="completed-checkbox-label">
                    <input
                      type="checkbox"
                      checked={showCompletedKesif}
                      onChange={(e) => setShowCompletedKesif(e.target.checked)}
                    />
                    <span>
                      {language === 'nl' ? 'Toon ook uitgevoerde / voltooide inmetingen' : 'Yapılan / Tamamlanan Keşifleri de Göster'} (<strong>{completedInspections.length}</strong>)
                    </span>
                  </label>
                </div>
              )}

              {filteredKesifEvents.length === 0 && (!showCompletedKesif || completedInspections.length === 0) ? (
                <div className="empty-schedule-state">
                  <i className="fas fa-calendar-check"></i>
                  <p>{language === 'nl' ? `Geen inmeetafspraak gevonden die voldoet aan de geselecteerde filters (${getFilterSummaryText()}).` : `Seçilen filtrelere uygun (${getFilterSummaryText()}) keşif randevusu bulunmuyor.`}</p>
                </div>
              ) : (
                <>
                  <TimelineSection
                    title={language === 'nl' ? 'Vertraagde Inmetingen' : 'Geciken Keşif Randevuları'}
                    icon="fa-exclamation-triangle"
                    variant="overdue"
                    events={filteredKesifEvents.filter(e => e.category === 'overdue')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Inmetingen Vandaag' : 'Bugünkü Keşifler'}
                    icon="fa-clock"
                    variant="today"
                    events={filteredKesifEvents.filter(e => e.category === 'today')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Inmetingen Morgen' : 'Yarınki Keşifler'}
                    icon="fa-calendar-day"
                    variant="tomorrow"
                    events={filteredKesifEvents.filter(e => e.category === 'tomorrow')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Inmetingen Deze Week' : 'Bu Haftaki Keşifler'}
                    icon="fa-calendar-week"
                    variant="thisWeek"
                    events={filteredKesifEvents.filter(e => e.category === 'thisWeek')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />
                  <TimelineSection
                    title={language === 'nl' ? 'Aankomende Inmetingen' : 'Gelecek Keşif Randevuları'}
                    icon="fa-calendar-alt"
                    variant="upcoming"
                    events={filteredKesifEvents.filter(e => e.category === 'upcoming')}
                    onCustomerClick={handleCustomerClick}
                    onToggleInspection={onToggleInspection}
                  />

                  {showCompletedKesif && completedInspections.length > 0 && (
                    <TimelineSection
                      title={language === 'nl' ? 'Voltooide Inmetingen (Gemarkeerd als voltooid)' : 'Tamamlanan Keşifler (Yapıldı Olarak İşaretlenenler)'}
                      icon="fa-check-circle"
                      variant="completed"
                      events={filterBySearch(filterByTime(completedInspections))}
                      onCustomerClick={handleCustomerClick}
                      onToggleInspection={onToggleInspection}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ===================== TAB 5: HARİTA & ADRES PİNLERİ ===================== */}
          {activeTab === 'map' && (
            <div className="map-view-container">
              {/* Harita Hızlı Eylem Barı */}
              <div className="map-actions-banner">
                <div className="map-banner-info">
                  <div className="map-banner-icon">
                    <i className="fas fa-map-marked-alt"></i>
                  </div>
                  <div className="map-banner-text">
                    <h4>{language === 'nl' ? 'Google Maps Pin & Route Centrum' : 'Google Haritalar Pinleme & Rota Merkezi'}</h4>
                    <p>
                      {language === 'nl'
                        ? 'Zet alle inmeet- en montageadressen vast op Google Maps. Met één klik kunt u alle haltes naar uw route exporteren of adressen afzonderlijk bekijken.'
                        : 'Montaj ve keşif yapılacak tüm adresleri Google Haritalar\'da pinleyin. Tek tıkla tüm durakları rotanıza aktarabilir veya adresleri tek tek pinleyebilirsiniz.'}
                    </p>
                  </div>
                </div>

                <div className="map-banner-buttons">
                  {allPinsUrl && (
                    <a
                      href={allPinsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-banner-btn primary-pin-btn"
                      title={language === 'nl' ? 'Open alle gefilterde adressen in Google Maps en toon de route' : "Filtrelenmiş tüm adresleri Google Haritalar'da aç ve rotayı göster"}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      <span>{language === 'nl' ? 'Alles op Kaart Openen' : 'Tümünü Haritada Aç'} ({visibleAddresses.length})</span>
                    </a>
                  )}

                  {kesifPinsUrl && visibleKesifAddresses.length > 0 && (
                    <a
                      href={kesifPinsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-banner-btn kesif-pin-btn"
                      title={language === 'nl' ? 'Alleen inmeetadressen openen in Google Maps' : "Sadece keşif adreslerini Google Haritalar'da aç"}
                    >
                      <i className="fas fa-ruler-combined"></i>
                      <span>{language === 'nl' ? 'Inmetingen Pinnen' : 'Keşifleri Pinle'} ({visibleKesifAddresses.length})</span>
                    </a>
                  )}

                  {montajPinsUrl && visibleMontajAddresses.length > 0 && (
                    <a
                      href={montajPinsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-banner-btn montaj-pin-btn"
                      title={language === 'nl' ? 'Alleen montageadressen openen in Google Maps' : "Sadece montaj adreslerini Google Haritalar'da aç"}
                    >
                      <i className="fas fa-truck"></i>
                      <span>{language === 'nl' ? 'Montages Pinnen' : 'Montajları Pinle'} ({visibleMontajAddresses.length})</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Canlı Harita Önizleme Iframe */}
              <div className="map-preview-wrapper">
                <div className="map-preview-header">
                  <div className="map-preview-title">
                    <i className="fas fa-compass"></i>
                    <span>{language === 'nl' ? 'Live Kaart Voorvertoning' : 'Canlı Harita Önizleme'} ({regionFilter ? (language === 'nl' ? `Filter: ${regionFilter}` : `Filtre: ${regionFilter}`) : (language === 'nl' ? 'Heel Nederland' : 'Hollanda Geneli')})</span>
                  </div>
                  <span className="map-preview-subtitle">
                    {visibleAddresses.length} {language === 'nl' ? (visibleAddresses.length === 1 ? 'Adres weergegeven' : 'Adressen weergegeven') : 'Adres Listeleniyor'}
                  </span>
                </div>
                <div className="map-iframe-container">
                  <iframe
                    title={language === 'nl' ? 'Google Maps Voorvertoning' : 'Google Haritalar Önizleme'}
                    className="map-iframe"
                    src={generateGoogleMapsEmbedUrl(mapEmbedQuery)}
                    loading="lazy"
                    allowFullScreen
                  />
                </div>
              </div>

              {/* Pinlenen Adres Kartları Listesi */}
              <div className="pinned-locations-section">
                <div className="section-header-row">
                  <h3 className="section-title">
                    <i className="fas fa-map-pin"></i> {language === 'nl' ? 'Vast te pinnen Adressen' : 'Pinlenebilir Adresler'} ({visibleAddresses.length})
                  </h3>
                  <span className="section-subtitle">
                    {language === 'nl' ? (
                      <>Klik op de knop <strong>Vastpinnen op Kaart</strong> op de kaarten om het adres direct in Google Maps te openen.</>
                    ) : (
                      <>Kartlardaki <strong>Haritada Pinle</strong> butonuna tıklayarak adresi Google Haritalar'da anında açabilirsiniz.</>
                    )}
                  </span>
                </div>

                {visibleAddresses.length === 0 ? (
                  <div className="empty-schedule-state">
                    <i className="fas fa-map-marker-slash"></i>
                    <p>{language === 'nl' ? 'Geen inmeet- of montagerecords met adres gevonden die voldoen aan de geselecteerde filters.' : 'Seçili filtrelere uygun adresi girilmiş keşif veya montaj kaydı bulunamadı.'}</p>
                  </div>
                ) : (
                  <div className="pinned-cards-grid">
                    {visibleAddresses.map(item => (
                      <PinnedCardItem
                        key={`pinned-${item.type}-${item.id}`}
                        item={item}
                        onCustomerClick={handleCustomerClick}
                        onToggleInspection={onToggleInspection}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Zaman Dilimi Bölümü
 */
const TimelineSection = ({ title, icon, variant, events = [], onCustomerClick, onToggleInspection }) => {
  if (!events || events.length === 0) return null;

  return (
    <div className={`timeline-section section-${variant}`}>
      <div className="timeline-section-header">
        <i className={`fas ${icon}`}></i>
        <h4>{title}</h4>
        <span className="section-badge">{events.length}</span>
      </div>
      <div className="timeline-items-grid">
        {events.map(item => (
          <ScheduleItemCard
            key={item.id}
            item={item}
            onCustomerClick={onCustomerClick}
            onToggleInspection={onToggleInspection}
            showTypeBadge
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Tek Bir Randevu Kartı (Responsive & Taşmaz)
 */
const ScheduleItemCard = ({ item, onCustomerClick, onToggleInspection, showTypeBadge = false }) => {
  const { t, language } = useLanguage();
  const isKesif = item.type === 'kesif';
  const cleanPhone = cleanPhoneNumber(item.phone);
  const waUrl = getWhatsAppDirectUrl(item.phone, item.name, item.type, item.formattedDate);
  const fullAddress = item.address ? `${item.address}${item.city ? `, ${item.city}` : ''} Netherlands`.trim() : '';
  const mapUrl = fullAddress ? generateGoogleMapsUrl([fullAddress], { startFromStore: false }) : null;

  return (
    <div className={`schedule-item-card ${isKesif ? 'item-kesif' : 'item-montaj'} ${item.category === 'overdue' ? 'item-overdue' : ''} ${item.isInspectionDone ? 'item-done' : ''}`}>
      {/* Üst Kısım: Müşteri Adı ve Rozetler */}
      <div className="item-header-row">
        <button
          type="button"
          className="item-customer-name-btn"
          onClick={() => onCustomerClick(item.customerId, item.name)}
          title={language === 'nl' ? 'Klantdetails openen' : 'Müşteri detayını aç'}
        >
          <span className="customer-name">{item.name}</span>
          <i className="fas fa-external-link-alt name-ext-icon"></i>
        </button>

        <div className="item-badges-group">
          {showTypeBadge && (
            <span className={`type-badge ${isKesif ? 'badge-kesif' : 'badge-montaj'}`}>
              <i className={`fas ${item.typeIcon}`}></i>
              {isKesif ? (language === 'nl' ? 'Inmeten' : 'Keşif') : (language === 'nl' ? 'Montage' : 'Montaj')}
            </span>
          )}

          {item.isInspectionDone ? (
            <span className="status-pill pill-success">
              <i className="fas fa-check"></i> {language === 'nl' ? 'Voltooid' : 'Yapıldı'}
            </span>
          ) : item.category === 'overdue' ? (
            <span className="status-pill pill-danger">
              {item.daysOverdue} {language === 'nl' ? 'dag(en) te laat' : 'gün gecikti'}
            </span>
          ) : item.category === 'today' ? (
            <span className="status-pill pill-today">{language === 'nl' ? 'Vandaag' : 'Bugün'}</span>
          ) : item.category === 'tomorrow' ? (
            <span className="status-pill pill-tomorrow">{language === 'nl' ? 'Morgen' : 'Yarın'}</span>
          ) : item.daysLeft !== undefined && item.daysLeft > 1 ? (
            <span className="status-pill pill-upcoming">{language === 'nl' ? `Nog ${item.daysLeft} dagen` : `${item.daysLeft} gün kaldı`}</span>
          ) : null}
        </div>
      </div>

      {/* Tarih & Saat */}
      <div className="item-meta-row">
        <div className="item-meta-entry">
          <i className="fas fa-calendar-day"></i>
          <span>{formatScheduleDateDisplay(item.rawDate || item.formattedDate, language)}</span>
        </div>
        {!isKesif && (item.orderStatus || item.orderStatusLabel) && (
          <div className="item-meta-entry status-meta">
            <i className="fas fa-tag"></i>
            <span>{t(`orderStatus.${item.orderStatus}`, item.orderStatusLabel || item.orderStatus)}</span>
          </div>
        )}
      </div>

      {/* Adres / Şehir */}
      {item.address && (
        <div className="item-address-row">
          <i className="fas fa-map-marker-alt"></i>
          <span className="address-text" title={item.address}>
            {item.city ? <strong>{item.city}: </strong> : ''}
            {item.address}
          </span>
        </div>
      )}

      {/* Hızlı Eylem Butonları */}
      <div className="item-actions-row">
        {isKesif && onToggleInspection && (
          <button
            type="button"
            className={`action-btn mark-done-btn ${item.isInspectionDone ? 'is-done' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleInspection(item.customerId, !item.isInspectionDone);
            }}
            title={item.isInspectionDone ? (language === 'nl' ? 'Gemarkeerd als inmeting voltooid (Klik om te herstellen)' : 'Keşif yapıldı olarak işaretli (Tekrar aktif etmek için tıklayın)') : (language === 'nl' ? 'Markeer inmeting als voltooid en verwijder uit lijst' : 'Keşfi yapıldı olarak işaretle ve listeden kaldır')}
          >
            <i className={`fas ${item.isInspectionDone ? 'fa-undo' : 'fa-check'}`}></i>
            <span>{item.isInspectionDone ? (language === 'nl' ? 'Herstellen' : 'Geri Al') : (language === 'nl' ? 'Inmeting Voltooid' : 'Keşif Yapıldı')}</span>
          </button>
        )}

        {cleanPhone && (
          <a
            href={`tel:${cleanPhone}`}
            className="action-btn call-btn"
            title={language === 'nl' ? 'Bellen' : 'Telefon ile ara'}
          >
            <i className="fas fa-phone"></i>
            <span>{language === 'nl' ? 'Bellen' : 'Ara'}</span>
          </a>
        )}

        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="action-btn wa-btn"
            title={language === 'nl' ? 'WhatsApp bericht sturen' : 'WhatsApp mesajı gönder'}
          >
            <i className="fab fa-whatsapp"></i>
            <span>WhatsApp</span>
          </a>
        )}

        {mapUrl && (
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="action-btn map-btn"
            title={language === 'nl' ? 'Toon in Google Maps en routebeschrijving opvragen' : "Google Haritalar'da göster ve yol tarifi al"}
          >
            <i className="fas fa-directions"></i>
            <span>{language === 'nl' ? 'Kaart' : 'Harita'}</span>
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Haritada Pinleme Kartı (PinnedCardItem)
 * Keşif ve Montaj adreslerini Google Haritalar'da pinleme ve rotalama kartı.
 */
const PinnedCardItem = ({ item, onCustomerClick, onToggleInspection }) => {
  const { language } = useLanguage();
  const isKesif = item.type === 'kesif';
  const cleanPhone = cleanPhoneNumber(item.phone);
  const waUrl = getWhatsAppDirectUrl(item.phone, item.name, item.type);
  const fullAddress = `${item.address || ''}${item.city ? `, ${item.city}` : ''} Netherlands`.trim();
  const pinUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;

  return (
    <div
      className={`pinned-card ${isKesif ? 'pinned-kesif' : 'pinned-montaj'} ${item.isInspectionDone ? 'is-completed' : ''}`}
      onClick={() => onCustomerClick && onCustomerClick(item.customerId, item.name)}
    >
      <div className="pinned-card-top">
        <span className={`pinned-type-badge ${isKesif ? 'badge-kesif' : 'badge-montaj'}`}>
          <i className={`fas ${isKesif ? 'fa-ruler-combined' : 'fa-truck'}`}></i>
          {isKesif ? (language === 'nl' ? 'Inmeten & Advies' : 'Keşif & Ölçüm') : (language === 'nl' ? 'Montage & Levering' : 'Montaj & Teslimat')}
        </span>
        <span className="pinned-date-badge">
          <i className="far fa-calendar-alt"></i>
          {item.rawDate ? formatScheduleDateDisplay(item.rawDate, language) : (item.formattedDate || item.timeLabel || item.dateLabel || (language === 'nl' ? 'Geen datum opgegeven' : 'Tarih belirtilmedi'))}
        </span>
      </div>

      <div className="pinned-card-body">
        <h4 className="pinned-customer-name">
          {item.name}
          {item.isInspectionDone && (
            <span className="pinned-done-tag"><i className="fas fa-check-circle"></i> {language === 'nl' ? 'Voltooid' : 'Tamamlandı'}</span>
          )}
        </h4>

        <div className="pinned-address-box">
          <i className="fas fa-map-marker-alt"></i>
          <div className="pinned-address-text">
            {item.city && <span className="pinned-city-tag">{item.city}</span>}
            <p>{item.address}</p>
          </div>
        </div>
      </div>

      <div className="pinned-card-actions" onClick={e => e.stopPropagation()}>
        <a
          href={pinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pinned-action-btn pin-direct-btn"
          title={language === 'nl' ? 'Zet dit adres vast op Google Maps' : "Google Haritalar'da bu adresi pinle"}
        >
          <i className="fas fa-map-pin"></i>
          <span>{language === 'nl' ? 'Vastpinnen op Kaart' : 'Haritada Pinle'}</span>
        </a>

        <a
          href={dirUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pinned-action-btn route-btn"
          title={language === 'nl' ? 'Routebeschrijving opvragen' : 'Yol tarifi al'}
        >
          <i className="fas fa-directions"></i>
          <span>{language === 'nl' ? 'Route' : 'Rota'}</span>
        </a>

        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pinned-action-btn wa-btn"
            title={language === 'nl' ? 'WhatsApp bericht sturen' : 'WhatsApp mesajı gönder'}
          >
            <i className="fab fa-whatsapp"></i>
          </a>
        )}

        {cleanPhone && (
          <a
            href={`tel:${cleanPhone}`}
            className="pinned-action-btn call-btn"
            title={language === 'nl' ? 'Bellen' : 'Telefonla ara'}
          >
            <i className="fas fa-phone"></i>
          </a>
        )}

        {isKesif && onToggleInspection && (
          <button
            type="button"
            className={`pinned-action-btn done-toggle-btn ${item.isInspectionDone ? 'is-done' : ''}`}
            onClick={() => onToggleInspection(item.customerId, !item.isInspectionDone)}
            title={item.isInspectionDone ? (language === 'nl' ? 'Inmeting voltooid (Klik om te herstellen)' : 'Keşif yapıldı olarak işaretli (Tekrar aktife al)') : (language === 'nl' ? 'Markeer inmeting als voltooid' : 'Keşfi yapıldı olarak işaretle')}
          >
            <i className={`fas ${item.isInspectionDone ? 'fa-undo' : 'fa-check'}`}></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(ScheduleReminders);

